// HAPPYCAP_INFO_LEGAL_BUTTONS_OUTLINE_V1
// HAPPYCAP_INFO_NAVBAR_ICONS_FINAL_V1
import { Link } from "react-router-dom";
import {
  Home, Users, User, Images,
  Bell, MessagesSquare, BookOpenText
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

// HAPPYCAP_INFO_HARMONIZED_V1
const InfoPage = () => {
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [termsData, setTermsData] = useState(null);
  const [privacyData, setPrivacyData] = useState(null);

  useEffect(() => {
    fetch(`/locales/${i18n.language}/terms.json`)
      .then((res) => res.json())
      .then(setTermsData);

    fetch(`/locales/${i18n.language}/privacy.json`)
      .then((res) => res.json())
      .then(setPrivacyData);
  }, [i18n.language]);

  if (!isAuthenticated) return null;

  return (
    <div
      className="mx-auto min-h-screen max-w-6xl px-4 pb-14 pt-8 text-[#163F63] sm:px-6 sm:pt-12"
      role="main"
      aria-label="Informations principales"
    >
      {/* HAPPYCAP_INFO_ICONS_OVER_PHOTOS_V1 */}
      <section className="relative mb-8 overflow-hidden rounded-[30px] bg-[#EAF3FB] px-6 py-10 shadow-sm sm:mb-10 sm:px-12 sm:py-14">
        <div
          className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-[#CBE5FA]/70"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-28 right-20 h-56 w-56 rounded-full bg-[#FFE3C5]/70"
          aria-hidden="true"
        />
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <div
            className="mx-auto mb-6 h-1.5 w-16 rounded-full bg-[#F5A85D]"
            aria-hidden="true"
          />
          <h1
            id="info-title"
            className="text-3xl font-extrabold leading-tight tracking-tight text-[#163F63] sm:text-4xl lg:text-5xl"
          >
            {t("info.title")}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-[#45647C] sm:text-lg">
            {t("info.intro")}
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" role="navigation" aria-labelledby="info-title">
        <NavItem image="resources" icon={<BookOpenText className="h-7 w-7" />} label={t("info.items.resources.label")} description={t("info.items.resources.desc")} accent="blue" />
        <NavItem image="posts" icon={<Home className="h-7 w-7" />} label={t("info.items.posts.label")} description={t("info.items.posts.desc")} accent="orange" />
        <NavItem image="friends" icon={<Users className="h-7 w-7" />} label={t("info.items.friends.label")} description={t("info.items.friends.desc")} accent="green" />
        <NavItem image="profile" icon={<User className="h-7 w-7" />} label={t("info.items.profile.label")} description={t("info.items.profile.desc")} accent="purple" />
        <NavItem image="gallery" icon={<Images className="h-7 w-7" />} label={t("info.items.gallery.label")} description={t("info.items.gallery.desc")} accent="orange" />
        <NavItem image="chat" icon={<MessagesSquare className="h-7 w-7" />} label={t("info.items.chat.label")} description={t("info.items.chat.desc")} accent="pink" />
        <NavItem image="notifications" icon={<Bell className="h-7 w-7" />} label={t("info.items.notifications.label")} description={t("info.items.notifications.desc")} accent="blue" wide />
      </div>

      <div className="mt-10 flex flex-row flex-wrap justify-center gap-3 rounded-[26px] border border-white/80 bg-white/75 px-5 py-7 text-center shadow-[0_8px_30px_rgba(22,63,99,0.045)] sm:mt-12 sm:gap-4" role="region" aria-label="Documents légaux">
        <button
          onClick={() => setShowTerms(true)}
          className="inline-flex min-h-12 items-center justify-center rounded-xl border border-[#A8CEE4] bg-transparent px-6 py-3 text-sm sm:text-base font-medium text-[#247BA8] transition-all duration-200 hover:border-[#247BA8] hover:bg-[#EAF3FB] hover:text-[#163F63] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]/30"
          aria-label={t("legal.terms_button")}
        >
          {t("legal.terms_button", "Conditions d’utilisation")}
        </button>

        <button
          onClick={() => setShowPrivacy(true)}
          className="inline-flex min-h-12 items-center justify-center rounded-xl border border-[#A8CEE4] bg-transparent px-6 py-3 text-sm sm:text-base font-medium text-[#247BA8] transition-all duration-200 hover:border-[#247BA8] hover:bg-[#EAF3FB] hover:text-[#163F63] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]/30"
          aria-label={t("legal.privacy_button")}
        >
          {t("legal.privacy_button", "Politique de confidentialité")}
        </button>
      </div>

      <Modal show={showTerms} onClose={() => setShowTerms(false)} content={termsData} />
      <Modal show={showPrivacy} onClose={() => setShowPrivacy(false)} content={privacyData} />
    </div>
  );
};

const NavItem = ({ image, icon, label, description, accent = "blue", wide = false }) => {
  const colors = {
    blue: "bg-[#E7F3FC] text-[#247BA8]",
    orange: "bg-[#FFF1E4] text-[#E78B32]",
    green: "bg-[#EAF7E9] text-[#48A45B]",
    purple: "bg-[#F0EAFF] text-[#8659D9]",
    pink: "bg-[#FDE9F0] text-[#DF5684]",
  };

  return (
    <article
      className={`group overflow-hidden rounded-[24px] border border-[#E8EDF3] bg-white shadow-[0_10px_35px_rgba(22,63,99,0.07)] transition-shadow duration-200 hover:shadow-[0_16px_40px_rgba(22,63,99,0.12)] ${wide ? "sm:col-span-2 lg:col-span-3 lg:flex" : ""}`}
    >
      <div
        className={`relative shrink-0 ${wide ? "lg:w-[42%]" : ""}`}
      >
        <img
          src={`/img/info_${image}.webp`}
          alt=""
          loading="lazy"
          className={`w-full object-cover ${wide ? "h-48 lg:h-60" : "h-44 sm:h-48"}`}
        />

        <span
          className={`absolute bottom-3 left-4 inline-flex h-16 w-16 items-center justify-center rounded-full border-4 border-white shadow-lg ${colors[accent]}`}
          aria-hidden="true"
        >
          {icon}
        </span>
      </div>

      <div className="flex flex-1 flex-col justify-center p-5 sm:p-6">
        <h2 className="text-xl font-bold leading-snug text-[#163F63]">
          {label}
        </h2>
        <p className="mt-2 text-sm leading-7 text-[#45647C] sm:text-base">
          {description}
        </p>
      </div>
    </article>
  );
};

const Modal = ({ show, onClose, content }) => {
  useEffect(() => {
    if (show) {
      setTimeout(() => {
        const focusable = document.querySelector("[data-modal-content]");
        if (focusable) focusable.focus();
      }, 0);
    }
  }, [show]);

  if (!show || !content) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl p-6 sm:p-8 relative"
        tabIndex={-1}
        data-modal-content
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-xl text-gray-600 hover:text-red-600"
          aria-label="Fermer"
        >
          ×
        </button>
        <h2 id="modal-title" className="text-2xl font-bold text-[#245580] mb-3">{content.title}</h2>
        <p className="text-sm text-gray-500 mb-4">{content.last_update}</p>
        {content.sections.map((section, idx) => (
          <div key={idx} className="mb-4">
            <h3 className="text-lg font-bold text-[#245580] mb-2">{section.title}</h3>
            {section.body && <p className="text-sm mb-2 whitespace-pre-line">{section.body}</p>}
            {section.list && (
              <ul className="list-disc ml-5 text-sm space-y-1">
                {section.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InfoPage;
